import type {
  ApprovalGate,
  CompanySnapshot,
  Department,
  DomainEvent,
  Grant,
  Job,
  JobStatus,
  Person,
  PersonNature,
  RunnerSession,
  RunnerNode,
} from '@mk1/protocol';

export type {
  ApprovalGate,
  CompanySnapshot,
  Department,
  DomainEvent,
  Grant,
  Job,
  JobStatus,
  Person,
} from '@mk1/protocol';

export class DomainInvariantError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'DomainInvariantError';
    this.code = code;
  }
}

export interface CompanyRepository {
  getSnapshot(): Promise<CompanySnapshot>;
  subscribe(listener: (snapshot: CompanySnapshot) => void): () => void;
  revokeTool(personId: string): Promise<DomainEvent> | DomainEvent;
}

const severityRank = { INCIDENT: 4, BLOCKER: 3, ATTENTION: 2, INFO: 1 } as const;
const priorityRank = { P0: 4, P1: 3, P2: 2, P3: 1 } as const;

export function getHighestBlocker(snapshot: CompanySnapshot): Job | undefined {
  return snapshot.jobs
    .filter((job) => job.blockedReason !== null)
    .toSorted((a, b) => priorityRank[b.priority] - priorityRank[a.priority])[0];
}

export function getTopNotification(snapshot: CompanySnapshot) {
  return snapshot.notifications.toSorted(
    (a, b) => severityRank[b.severity] - severityRank[a.severity],
  )[0];
}

export function getDepartment(snapshot: CompanySnapshot, id: string): Department | undefined {
  return snapshot.departments.find((department) => department.id === id);
}

export function getDepartmentPeople(snapshot: CompanySnapshot, id: string): Person[] {
  return snapshot.people.filter((person) => person.departmentId === id);
}

export function getPerson(snapshot: CompanySnapshot, id: string): Person | undefined {
  return snapshot.people.find((person) => person.id === id);
}

export function getPersonJob(snapshot: CompanySnapshot, id: string): Job | undefined {
  return snapshot.jobs.find((job) => job.ownerId === id && job.status !== 'COMPLETED');
}

export function getPersonSession(snapshot: CompanySnapshot, id: string): RunnerSession | undefined {
  return snapshot.sessions.find((session) => session.personId === id);
}

export function deriveKpis(snapshot: CompanySnapshot) {
  const activeStatuses: Job['status'][] = ['ASSIGNED', 'RUNNING', 'QA_REVIEW'];
  return {
    activeJobs: snapshot.jobs.filter((job) => activeStatuses.includes(job.status)).length,
    waitingApprovals: snapshot.approvals.filter((gate) => gate.status === 'PENDING').length,
    blockedJobs: snapshot.jobs.filter((job) => job.blockedReason !== null).length,
    humanMinutes: snapshot.project.humanMinutesToday,
    spendEur: snapshot.project.actualSpendEur,
    queueSla:
      snapshot.source.mode === 'LIVE_LOCAL' || snapshot.jobs.length === 0 ? 'unknown' : '87%',
  };
}

export function validateOrganization(snapshot: CompanySnapshot): string[] {
  const issues: string[] = [];
  const ids = new Set(snapshot.people.map((person) => person.id));
  if (snapshot.people.length !== 11) issues.push('Expected exactly 11 current people.');
  for (const person of snapshot.people) {
    if (person.managerId && !ids.has(person.managerId))
      issues.push(`${person.id}: manager missing`);
  }
  if (snapshot.people.filter((person) => person.nature === 'HUMAN').length !== 2)
    issues.push('Expected exactly two humans.');
  if (snapshot.people.filter((person) => person.nature === 'PERMANENT_AI').length !== 7)
    issues.push('Expected exactly seven permanent AI personas.');
  if (snapshot.people.filter((person) => person.nature === 'AIZUBI').length !== 2)
    issues.push('Expected exactly two AiZubis.');
  return issues;
}

const ordinaryFlow: Record<JobStatus, readonly JobStatus[]> = {
  DRAFT: ['READY', 'BLOCKED', 'CANCELLED'],
  READY: ['QUEUED', 'BLOCKED', 'CANCELLED', 'SUSPENDED'],
  QUEUED: ['ASSIGNED', 'WAITING_DEPENDENCY', 'BLOCKED', 'CANCELLED', 'SUSPENDED'],
  ASSIGNED: ['RUNNING', 'WAITING_TOOL', 'WAITING_HUMAN', 'BLOCKED', 'CANCELLED', 'SUSPENDED'],
  RUNNING: [
    'WAITING_TOOL',
    'WAITING_HUMAN',
    'WAITING_DEPENDENCY',
    'QA_REVIEW',
    'BLOCKED',
    'FAILED',
    'CANCELLED',
    'SUSPENDED',
  ],
  WAITING_HUMAN: ['ASSIGNED', 'RUNNING', 'WAITING_HUMAN_EXPIRED', 'BLOCKED', 'CANCELLED'],
  WAITING_HUMAN_EXPIRED: ['WAITING_HUMAN', 'CANCELLED', 'BLOCKED'],
  WAITING_TOOL: ['ASSIGNED', 'RUNNING', 'BLOCKED', 'CANCELLED', 'SUSPENDED'],
  WAITING_DEPENDENCY: ['READY', 'QUEUED', 'BLOCKED', 'CANCELLED'],
  QA_REVIEW: ['APPROVED', 'CHANGES_REQUESTED', 'BLOCKED', 'FAILED', 'CANCELLED'],
  APPROVED: ['COMPLETED', 'BLOCKED', 'CANCELLED'],
  CHANGES_REQUESTED: ['ASSIGNED', 'RUNNING', 'CANCELLED'],
  COMPLETED: [],
  BLOCKED: ['READY', 'QUEUED', 'ASSIGNED', 'CANCELLED', 'SUSPENDED'],
  FAILED: ['RETRY_QUEUED', 'CANCELLED'],
  RETRY_QUEUED: ['QUEUED', 'CANCELLED'],
  CANCELLED: [],
  SUSPENDED: ['READY', 'QUEUED', 'CANCELLED'],
};

export interface JobTransitionContext {
  from: JobStatus;
  to: JobStatus;
  hasActiveAssignment: boolean;
  hasActiveLease: boolean;
  risk: Job['risk'];
  producerId: string;
  qaReviewerId: string | null;
  deliverableCount: number;
  acceptanceEvidenceCount: number;
  externalAction: boolean;
  gateConsumed: boolean;
}

export function allowedJobTransitions(from: JobStatus): readonly JobStatus[] {
  return ordinaryFlow[from];
}

export function assertJobTransition(context: JobTransitionContext): void {
  if (!ordinaryFlow[context.from].includes(context.to)) {
    throw new DomainInvariantError(
      'ILLEGAL_JOB_TRANSITION',
      `Job cannot transition from ${context.from} to ${context.to}.`,
    );
  }
  if (context.to === 'RUNNING' && (!context.hasActiveAssignment || !context.hasActiveLease)) {
    throw new DomainInvariantError(
      'RUNNING_REQUIRES_ASSIGNMENT_AND_LEASE',
      'RUNNING requires both an active assignment and an active tool lease.',
    );
  }
  if (
    context.to === 'APPROVED' &&
    ['R3', 'R4'].includes(context.risk) &&
    context.qaReviewerId === context.producerId
  ) {
    throw new DomainInvariantError(
      'HIGH_IMPACT_SELF_QA',
      'The sole producer cannot approve high-impact work.',
    );
  }
  if (
    context.to === 'COMPLETED' &&
    (context.deliverableCount === 0 || context.acceptanceEvidenceCount === 0)
  ) {
    throw new DomainInvariantError(
      'COMPLETION_REQUIRES_EVIDENCE',
      'COMPLETED requires a deliverable and acceptance evidence.',
    );
  }
  if (context.to === 'COMPLETED' && context.externalAction && !context.gateConsumed) {
    throw new DomainInvariantError(
      'EXTERNAL_ACTION_REQUIRES_CONSUMED_GATE',
      'External-action work requires a consumed Human Gate before completion.',
    );
  }
}

export interface LeaseEligibility {
  grant: Pick<Grant, 'status' | 'expiry' | 'accessLevel'>;
  now: Date;
  subjectNature: PersonNature;
}

export function assertLeaseEligibility({ grant, now, subjectNature }: LeaseEligibility): void {
  if (!['ACTIVE', 'ACTIVE_BOUNDED'].includes(grant.status)) {
    throw new DomainInvariantError('GRANT_NOT_ACTIVE', 'Only an active grant can create a lease.');
  }
  if (Date.parse(grant.expiry) <= now.getTime()) {
    throw new DomainInvariantError('GRANT_EXPIRED', 'An expired grant cannot create a lease.');
  }
  if (grant.accessLevel === 'A6' && subjectNature !== 'HUMAN') {
    throw new DomainInvariantError('A6_HUMAN_ONLY', 'A6 can never be assigned to an AI identity.');
  }
}

const grantFlow: Record<Grant['status'], readonly Grant['status'][]> = {
  REQUESTED: ['APPROVED', 'DENIED', 'EXPIRED', 'REVOKED'],
  APPROVED: ['ACTIVE', 'ACTIVE_BOUNDED', 'DENIED', 'EXPIRED', 'REVOKED'],
  ACTIVE: ['EXPIRING', 'EXPIRED', 'REVOKED', 'SUSPENDED'],
  ACTIVE_BOUNDED: ['EXPIRING', 'EXPIRED', 'REVOKED', 'SUSPENDED'],
  EXPIRING: ['EXPIRED', 'REVOKED', 'SUSPENDED'],
  EXPIRED: [],
  DENIED: [],
  REVOKED: [],
  SUSPENDED: ['ACTIVE', 'ACTIVE_BOUNDED', 'REVOKED', 'EXPIRED'],
  PREVIEW_REVOKED: [],
};

export function assertGrantTransition(from: Grant['status'], to: Grant['status']): void {
  if (!grantFlow[from].includes(to)) {
    throw new DomainInvariantError(
      'ILLEGAL_GRANT_TRANSITION',
      `Grant cannot transition from ${from} to ${to}.`,
    );
  }
}

export function assertGrantLevelAllowed(level: Grant['accessLevel'], nature: PersonNature): void {
  if (level === 'A6' && nature !== 'HUMAN') {
    throw new DomainInvariantError('A6_HUMAN_ONLY', 'A6 can never be assigned to an AI identity.');
  }
  if (level === 'A4' && nature === 'AIZUBI') {
    throw new DomainInvariantError(
      'AIZUBI_EXTERNAL_SEND_DENIED',
      'AiZubis cannot receive external-send authority.',
    );
  }
}

export function assertExternalActionActor(nature: PersonNature): void {
  if (nature !== 'HUMAN') {
    throw new DomainInvariantError(
      'AI_EXTERNAL_SEND_DENIED',
      'AI and AiZubi identities cannot perform an external-send action.',
    );
  }
}

export function approvalIsValidForPayload(
  gate: Pick<ApprovalGate, 'status' | 'payloadHash' | 'approvedPayloadHash'>,
): boolean {
  return (
    ['APPROVED_ONCE', 'APPROVED_BOUNDED'].includes(gate.status) &&
    gate.approvedPayloadHash !== null &&
    gate.approvedPayloadHash === gate.payloadHash
  );
}

export function nextGateAfterPayloadChange(
  gate: Pick<ApprovalGate, 'status' | 'payloadHash' | 'approvedPayloadHash'>,
  newPayloadHash: string,
): Pick<ApprovalGate, 'status' | 'payloadHash' | 'approvedPayloadHash'> {
  if (newPayloadHash === gate.payloadHash) return gate;
  return { status: 'PENDING', payloadHash: newPayloadHash, approvedPayloadHash: null };
}

export function assertGateConsumable(
  gate: Pick<ApprovalGate, 'status' | 'payloadHash' | 'approvedPayloadHash'> &
    Partial<Pick<ApprovalGate, 'expiresAt' | 'consumedAt' | 'scope'>>,
  now = new Date(),
  requestedScope?: Record<string, unknown>,
): void {
  if (gate.status === 'CONSUMED' || (gate.consumedAt !== undefined && gate.consumedAt !== null)) {
    throw new DomainInvariantError('GATE_ALREADY_CONSUMED', 'A one-time Human Gate is consumed.');
  }
  if (
    gate.expiresAt !== undefined &&
    gate.expiresAt !== null &&
    Date.parse(gate.expiresAt) <= now.getTime()
  ) {
    throw new DomainInvariantError('GATE_EXPIRED', 'An expired Human Gate cannot be consumed.');
  }
  if (!approvalIsValidForPayload(gate)) {
    throw new DomainInvariantError(
      'STALE_OR_UNAPPROVED_GATE',
      'The Human Gate is not approved for the current payload hash.',
    );
  }
  if (gate.status === 'APPROVED_BOUNDED' && requestedScope) {
    for (const [key, value] of Object.entries(requestedScope)) {
      if (JSON.stringify(gate.scope?.[key]) !== JSON.stringify(value)) {
        throw new DomainInvariantError(
          'GATE_SCOPE_MISMATCH',
          `Bounded Human Gate does not cover requested scope field ${key}.`,
        );
      }
    }
  }
}

const secretFieldPattern = /(password|passphrase|token|secret|cookie|private.?key|api.?key)/iu;

export function assertOrdinaryResourceMetadataSafe(metadata: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(metadata)) {
    if (secretFieldPattern.test(key)) {
      throw new DomainInvariantError(
        'RAW_SECRET_FIELD_FORBIDDEN',
        `Ordinary asset metadata cannot contain a ${key} field.`,
      );
    }
    if (typeof value === 'string' && secretFieldPattern.test(value) && value.length > 24) {
      throw new DomainInvariantError(
        'RAW_SECRET_VALUE_SUSPECTED',
        'Ordinary asset metadata cannot contain credential-like values.',
      );
    }
  }
}

const queueStatus = new Set<JobStatus>(['READY', 'QUEUED', 'RETRY_QUEUED']);

export function orderQueueJobs(jobs: readonly Job[]): Job[] {
  return jobs
    .filter((job) => queueStatus.has(job.status))
    .toSorted((left, right) => {
      const priority = priorityRank[right.priority] - priorityRank[left.priority];
      if (priority !== 0) return priority;
      const deadline = Date.parse(left.deadline) - Date.parse(right.deadline);
      if (deadline !== 0) return deadline;
      const leftQueued = left.queuedAt ? Date.parse(left.queuedAt) : Number.MAX_SAFE_INTEGER;
      const rightQueued = right.queuedAt ? Date.parse(right.queuedAt) : Number.MAX_SAFE_INTEGER;
      if (leftQueued !== rightQueued) return leftQueued - rightQueued;
      return left.id.localeCompare(right.id);
    });
}

export function dependencyBlockReason(predecessor: Pick<Job, 'id' | 'status'>): string | null {
  if (predecessor.status === 'COMPLETED') return null;
  if (predecessor.status === 'CANCELLED') {
    return `Dependency ${predecessor.id} was cancelled; successor cannot be claimed.`;
  }
  if (['FAILED', 'BLOCKED', 'SUSPENDED'].includes(predecessor.status)) {
    return `Dependency ${predecessor.id} is ${predecessor.status}; safe resolution is required.`;
  }
  return `Waiting for dependency ${predecessor.id} to complete.`;
}

export function assertRetryAvailable(retryCount: number, retryLimit: number): void {
  if (retryCount >= retryLimit) {
    throw new DomainInvariantError(
      'RETRY_BUDGET_EXHAUSTED',
      `Retry budget exhausted (${retryCount}/${retryLimit}).`,
    );
  }
}

export function runnerHeartbeatIsFresh(
  runner: Pick<RunnerNode, 'state' | 'lastHeartbeatAt'>,
  now: Date,
  timeoutMs = 30_000,
): boolean {
  return (
    runner.state === 'ONLINE' &&
    runner.lastHeartbeatAt !== null &&
    now.getTime() - Date.parse(runner.lastHeartbeatAt) <= timeoutMs
  );
}
